import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { createNoise3D } from 'simplex-noise';
import styles from './LoadingBar.module.scss';
import { inOutQuad } from '@support/animation';

interface NoiseReferencePoint {
  x: number;
  y: number;
}

interface WaveState {
  cpX: number;
  cpY: number;
  anchorX: number;
  anchorY: number;
}

const noise3d = createNoise3D();

const updateWave = ({ x, y }: NoiseReferencePoint, step: number): WaveState => {
  const noise = (offset: number) => (noise3d(x + offset, y + offset, step) + 1) / 2;
  const anchorX = noise(5) * 50 + 15;
  const cpX = anchorX / 2;
  const cpY = noise(1) * 2 - 0.5;
  const anchorY = noise(1) * 2 - 2;

  return {
    cpX,
    cpY,
    anchorX,
    anchorY,
  };
};

const easeFromTo = (from: number, to: number, proportion: number): number => {
  const delta = to - from;
  const easedProportion = inOutQuad(proportion);
  return from + delta * easedProportion;
};

const Wave = ({ step, magnitude, gradient }) => {
  const refPt = useRef<NoiseReferencePoint>({ x: Math.random() * 100, y: Math.random() * 100 });
  let { cpX, cpY, anchorX, anchorY } = { cpX: 25, cpY: 0, anchorX: 50, anchorY: 0 };
  if (magnitude > 0) {
    const pts = updateWave(refPt.current, step);
    cpX = easeFromTo(cpX, pts.cpX, magnitude);
    cpY = easeFromTo(cpY, pts.cpY, magnitude);
    anchorX = easeFromTo(anchorX, pts.anchorX, magnitude);
    anchorY = easeFromTo(anchorY, pts.anchorY, magnitude);
  }

  return (
    <>
      <g className={styles.waveBlurred}>
        <path
          vectorEffect="non-scaling-stroke"
          strokeWidth="2"
          stroke={`url(#${gradient})`}
          fill={`url(#${gradient})`}
          d={`M 0 0
            C 20 0 ${cpX} ${cpY} ${anchorX} ${anchorY}
            S 80 0 100 0
            L 100 0
            C 80 0 ${100 - cpX} ${cpY} ${anchorX} ${-anchorY}
            S 20 0 0 0
            Z`}
        />
      </g>
      <path
        vectorEffect="non-scaling-stroke"
        strokeWidth="1"
        stroke={`url(#${gradient})`}
        fill="none"
        d={`M 0 0
          C 20 0 ${cpX} ${cpY} ${anchorX} ${anchorY}
          S 80 0 100 0`}
      />
    </>
  );
};

export default function NexusLoadingBar({ loading }: { loading: boolean }) {
  // const loading = true;
  const loadingRef = useRef(loading);
  const rafID = useRef<number>();
  const prevTime = useRef<number>();
  const [step, setStep] = useState(1);
  // from 0 to 1 based on loading state
  const [magnitude, setMagnitude] = useState(0);
  const [isSafari, setIsSafari] = useState(false);

  const update = (time) => {
    if (prevTime.current != undefined) {
      const deltaTime = time - prevTime.current;
      setStep((prevStep) => prevStep + deltaTime * 0.0006);
      setMagnitude((prevMag) => {
        if (loadingRef.current && prevMag < 1) {
          return prevMag + deltaTime * 0.0005;
        } else if (!loadingRef.current && prevMag > 0) {
          return prevMag - deltaTime * 0.0005;
        }
        return prevMag;
      });
    }
    prevTime.current = time;
    rafID.current = requestAnimationFrame(update);
  };

  useEffect(() => {
    rafID.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafID.current);
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    if (!!(window as any).safari) {
      setIsSafari(true);
    }
  }, []);

  return (
    <svg
      className={cn(styles.loadingBar, !loading && styles.idle, isSafari && styles.safari)}
      viewBox="0 -5 100 10"
      preserveAspectRatio="none"
    >
      <defs>
        {/* green: #90E0C4, light purple: #A2B7E7, purple: #784DDA */}
        <linearGradient
          x1="50%"
          y1="1"
          x2="50%"
          y2="0"
          id="loaderGrad1"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#784DDA" offset="0%">
            <animate
              attributeName="stop-color"
              values="#A2B7E7; #90E0C4; #A2B7E7;"
              dur="6s"
              repeatCount="indefinite"
            />
          </stop>
          <stop stopColor="#784DDA" offset="100%">
            <animate
              attributeName="stop-color"
              values="#784DDA; #A2B7E7; #784DDA;"
              dur="7s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
        <linearGradient
          x1="50%"
          y1="1"
          x2="50%"
          y2="0"
          id="loaderGrad2"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#784DDA" offset="0%">
            <animate
              attributeName="stop-color"
              values="#784DDA; #A2B7E7; #784DDA;"
              dur="7s"
              repeatCount="indefinite"
            />
          </stop>
          <stop stopColor="#784DDA" offset="100%">
            <animate
              attributeName="stop-color"
              values="#A2B7E7; #90E0C4; #A2B7E7;"
              dur="6s"
              repeatCount="indefinite"
            />
          </stop>
        </linearGradient>
      </defs>
      <g className={styles.rearWave}>
        <Wave step={step} magnitude={magnitude} gradient="loaderGrad1" />
      </g>
      <Wave step={step} magnitude={magnitude} gradient="loaderGrad2" />
    </svg>
  );
}
